/**
 * DecisionFlow — RoleAdoptionModal
 * ui/src/components/admin/RoleAdoptionModal.jsx
 * S615
 *
 * Role code + name input modal triggered from RoleLibraryPopup "Add to My Roles →".
 * POST /api/v1/role-library/adopt
 *
 * Spec authority: crl_spec_v1_0.md §10
 * Props:
 *   role       — ref_role_library row (provides ref_role_id + display_name for defaults)
 *   onSuccess  — () => void — called after successful adopt
 *   onClose    — () => void
 *
 * Error handling:
 *   ROLE_DUPLICATE       — inline: "Role code already exists. Use a different code."
 *   MOTION_NOT_SEEDED    — inline: "Selling motions must be configured before adding roles."
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind. Zero dark: variants.
 * No <form> tags.
 */

import { useState, useEffect, useRef } from "react";
import { X, Loader2, ArrowRight } from "lucide-react";

const API = "/api/v1";

// Suggest a default role_code from display_name:
// "Enterprise Account Executive" → "ENT_AE" is too complex to auto-derive cleanly,
// so we just uppercase + replace spaces/special chars + truncate.
function suggestCode(displayName) {
  return displayName
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 20);
}

export default function RoleAdoptionModal({ role, onSuccess, onClose }) {
  const [roleCode, setRoleCode]   = useState(suggestCode(role.display_name));
  const [roleName, setRoleName]   = useState(role.display_name);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const inputRef                  = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Focus role code input on mount
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleAdopt = async () => {
    const code = roleCode.trim().toUpperCase();
    if (!code) { setError("Role code is required."); return; }
    if (!/^[A-Z0-9_-]+$/.test(code)) { setError("Uppercase letters, digits, underscores, and hyphens only."); return; }
    if (code.length > 20) { setError("Role code must be 20 characters or fewer."); return; }

    setSaving(true);
    setError(null);

    try {
      const r = await fetch(`${API}/role-library/adopt`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ref_role_id: role.ref_role_id,
          role_code:   code,
          role_name:   roleName.trim() || role.display_name,
        }),
      });

      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const errCode = body?.detail?.error?.code;
        if (errCode === "ROLE_DUPLICATE") {
          setError(`Role code ${code} already exists. Use a different code.`);
        } else if (errCode === "MOTION_NOT_SEEDED") {
          setError("Selling motions must be configured before adding roles. Go to the Selling Motions tab first.");
        } else {
          setError(body?.detail?.error?.message ?? `Error ${r.status}`);
        }
        setSaving(false);
        return;
      }

      onSuccess();
    } catch (e) {
      setError(e.message ?? "An unexpected error occurred.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-[420px] rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4">
          <div>
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Add {role.display_name}</h3>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Adopt this archetype into your role library.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">

          {error && (
            <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-3 py-2.5">
              <p className="text-tok-xs text-[rgb(var(--color-danger))]">{error}</p>
            </div>
          )}

          {/* Role Code */}
          <div>
            <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))] mb-1">
              Role Code <span className="text-[rgb(var(--color-danger))]">*</span>
            </label>
            <input
              ref={inputRef}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm font-mono text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              value={roleCode}
              onChange={e => { setRoleCode(e.target.value.toUpperCase()); setError(null); }}
              maxLength={20}
              placeholder="AE_ENT"
            />
            <p className="text-[10px] text-[rgb(var(--color-text-muted))] mt-1">
              Uppercase letters, digits, hyphens only. Max 20 characters. Immutable after creation.
            </p>
          </div>

          {/* Role Name */}
          <div>
            <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))] mb-1">Role Name</label>
            <input
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              placeholder={role.display_name}
              maxLength={100}
            />
            <p className="text-[10px] text-[rgb(var(--color-text-muted))] mt-1">Override the display name if needed. Defaults to the archetype name.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[rgb(var(--color-border))] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdopt}
            disabled={saving || !roleCode.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white disabled:opacity-50 hover:opacity-90"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>Add Role <ArrowRight className="h-3.5 w-3.5" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
